import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ContextService } from '@maximizer/core/shared/data-access';
import { Octopus, OpportunityForm } from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';
import {
  OpportunityAbEntryDetailsReadRequest,
  OpportunityAbEntryDetailsResponse,
  OpportunityCard,
  OpportunityCardMapper,
  OpportunityCreateMapper,
  OpportunityRevenueSum,
  OpportunityRevenueSumMapper,
  OpportunitySummary,
  OpportunitySummaryMapper,
  OpportunitySummaryResponse,
} from '@maximizer/outlook/shared/domain';
import { escapeCharacters } from '@maximizer/shared/util';

@Injectable()
export class OpportunityService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  save(opportunityForm: OpportunityForm): Observable<string | null> {
    const mapper = new OpportunityCreateMapper();
    const data = mapper.from(opportunityForm);
    const request: Octopus.OpportunityWriteRequest = {
      Opportunity: {
        Data: data,
      },
    };
    return this.http
      .post<Octopus.OpportunityWriteResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.Opportunity.Data.Key ?? null;
          }

          throw new HttpErrorResponse({ error: 'Unsuccessful request' });
        }),
      );
  }

  getSummary(key: string): Observable<OpportunitySummary> {
    const query = this.getLogicalQuery(key, true);
    const fields = this.getOpportunityScope();
    const request = {
      Configuration: Octopus.OpportunityReadDriver,
      Opportunity: {
        Scope: {
          Fields: fields,
        },
        Criteria: {
          SearchQuery: query,
          Top: 1,
        },
        OrderBy: {
          Fields: [{ LastModifyDate: 'DESC' }],
        },
      },
      'Opportunity.Count': {
        Scope: {
          Fields: {
            Key: 1,
            ForecastRevenue: {
              Value: 1,
              DisplayValue: 1,
              CurrencyCode: 1,
            },
          },
        },
        Criteria: {
          SearchQuery: query,
        },
      },

      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<OpportunitySummaryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new OpportunitySummaryMapper();
            return mapper.from(result);
          }

          return {
            opportunitiesAmount: 0,
            opportunitiesWorthValue: 0,
          };
        }),
      );
  }

  getOpenSum(key: string): Observable<OpportunityRevenueSum> {
    const openQuery: Octopus.LogicalQuery<Octopus.Opportunity> = {
      $OR: [{ AbEntryKey: { $EQ: key } }, { ContactKey: { $EQ: key } }],
      $AND: [{ Status: { $IN: [0, 2] } }],
    };
    const request: OpportunityAbEntryDetailsReadRequest = {
      Configuration: Octopus.OpportunityReadDriver,
      'Opportunity.Open': {
        Scope: {
          Fields: {
            '$SUM(corporateRevenue)/displayValue': 1,
          },
        },
        Criteria: {
          SearchQuery: openQuery,
        },
      },
    };

    return this.http
      .post<OpportunityAbEntryDetailsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(map((result) => this.mapCompletedAndOpenOpportunitySum(result)));
  }

  getOpenAndWonSum(key: string): Observable<OpportunityRevenueSum> {
    const wonQuery: Octopus.LogicalQuery<Octopus.Opportunity> = {
      $OR: [{ AbEntryKey: { $EQ: key } }, { ContactKey: { $EQ: key } }],
      $AND: [{ Status: { $IN: [3] } }],
    };

    const openQuery: Octopus.LogicalQuery<Octopus.Opportunity> = {
      $OR: [{ AbEntryKey: { $EQ: key } }, { ContactKey: { $EQ: key } }],
      $AND: [{ Status: { $IN: [0, 2] } }],
    };
    const request: OpportunityAbEntryDetailsReadRequest = {
      Configuration: Octopus.OpportunityReadDriver,
      Opportunity: {
        Scope: {
          Fields: {
            '$SUM(corporateRevenue)/displayValue': 1,
          },
        },
        Criteria: {
          SearchQuery: wonQuery,
        },
      },
      'Opportunity.Open': {
        Scope: {
          Fields: {
            '$SUM(corporateRevenue)/displayValue': 1,
          },
        },
        Criteria: {
          SearchQuery: openQuery,
        },
      },
    };

    return this.http
      .post<OpportunityAbEntryDetailsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(map((result) => this.mapCompletedAndOpenOpportunitySum(result)));
  }

  private mapCompletedAndOpenOpportunitySum(
    result: OpportunityAbEntryDetailsResponse,
  ) {
    if (result.Code === Octopus.ResponseStatusCode.Successful) {
      const mapper = new OpportunityRevenueSumMapper();
      return mapper.from(result);
    }

    return {
      current: '$0',
      completed: '$0',
    };
  }

  getOpportunityByContactId(
    key: string | string[],
  ): Observable<OpportunityCard[]> {
    const query = this.getLogicalQuery(key);
    const scope = this.getOpportunityScope();
    const request = {
      Configuration: Octopus.OpportunityReadDriver,
      Globalization: Octopus.DefaultGlobalization,
      Opportunity: {
        Scope: {
          Fields: scope,
        },
        Criteria: {
          SearchQuery: query,
        },
        OrderBy: {
          Fields: [{ LastModifyDate: 'DESC' }],
        },
      },
    };

    return this.http
      .post<Octopus.OpportunityResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new OpportunityCardMapper();
            const opportunities = result.Opportunity.Data || [];
            return opportunities.map((opportunity) => mapper.from(opportunity));
          }
          return [];
        }),
      );
  }

  search(
    searchPhrase: string,
    useWildcard = false,
    take = 15,
  ): Observable<OpportunityCard[]> {
    const searchEscapeCharacters = '-:[]';
    const scope = this.getOpportunityScope();
    const request: Octopus.OpportunityReadRequest = {
      Opportunity: {
        Criteria: {
          SearchQuery: {
            $PHRASE: `${escapeCharacters(searchPhrase, searchEscapeCharacters)}${!useWildcard ? '' : '*'}`,
          },
          Top: take,
        },
        OrderBy: {
          Fields: [{ Status: 'ASC' }, { LastModifyDate: 'DESC' }],
        },
        Scope: {
          Fields: scope,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Configuration: Octopus.OpportunityReadDriver,
    };

    return this.http
      .post<Octopus.OpportunityResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new OpportunityCardMapper();
            const opportunities = result.Opportunity.Data || [];
            return opportunities.map((opportunity) => mapper.from(opportunity));
          }
          return [];
        }),
      );
  }

  private getLogicalQuery(
    key: string | string[],
    onlyOpen: boolean = false,
  ): Octopus.LogicalQuery<Octopus.Opportunity> {
    const baseQuery =
      Array.isArray(key) && key.length
        ? {
            $OR: [{ AbEntryKey: { $IN: key } }, { ContactKey: { $IN: key } }],
          }
        : {
            $OR: [{ AbEntryKey: { $EQ: key } }, { ContactKey: { $EQ: key } }],
          };

    if (onlyOpen) {
      return {
        ...baseQuery,
        $AND: [{ Status: { $IN: [0, 2] } }],
      };
    }

    return baseQuery;
  }

  private getOpportunityScope(): Octopus.Scope<Octopus.Opportunity> {
    return {
      Key: 1,
      Objective: 1,
      Description: 1,
      ParentKey: 1,
      Owner: {
        DisplayValue: 1,
      },
      AbEntry: {
        Key: 1,
        CompanyName: 1,
        FirstName: 1,
        LastName: 1,
        Type: 1,
      },
      Contact: {
        Key: 1,
        FirstName: 1,
        LastName: 1,
      },
      ForecastRevenue: {
        Value: 1,
        DisplayValue: 1,
        CurrencyCode: 1,
      },
      StartDate: {
        Value: 1,
        DisplayValue: 1,
      },
      CloseDate: {
        Value: 1,
        DisplayValue: 1,
      },
      LastModifyDate: {
        Value: 1,
      },
      CurrentSalesStage: {
        ProbabilityClose: 1,
        DisplayValue: 1,
      },
      Leader: {
        Key: 1,
        DisplayValue: 1,
      },
      Status: 1,
      CurrentSalesStageKey: 1,
    };
  }
}
